import axios from 'axios'

import { COP_API_BASE_URL, USERS_ENDPOINT } from '../constants/service-endpoints'
import { buildCopHeaders, getSilentToken } from './tokenUtils'
import { post, get, put } from './http.service'

export interface User {
  id: number
  email: string
  firstname: string
  lastname: string
  function: string
  isSystemAdmin: boolean
  isGAShieldUser: boolean
  isUserManager: boolean
  isMultiTenantUser: boolean
  isAssignedToAnyTenant: boolean
  partnerPortalAccess: string
  state: string
  latestActivity: string | null
  defaultAccountId: string
  roleId: number | null
  roleName: string | null
  products: string[] | null
  cspAccess: boolean
  psaAccess: boolean
  userSlas: unknown[]
  isPowershopUser: boolean | null
}

export interface UserListResponse {
  '@odata.count'?: number
  value: User[]
}
export interface ExportUsersParams {
  userIds: (string | number)[];
}


export async function fetchUsers(): Promise<UserListResponse | null> {
  try {
    const token = getSilentToken()
    const url = `${COP_API_BASE_URL}${USERS_ENDPOINT}`
    const headers = buildCopHeaders(token)

    const response = await axios.get<User[]>(url, { headers })
    const value = response.data
    return { '@odata.count': value.length, value }
  } catch (error) {
    // eslint-disable-next-line no-console
    window.console.error('Users API error:', error)
    return null
  }
}
export async function exportUsers(params: ExportUsersParams): Promise<void> {
  const { userIds } = params

  try {
    const token = getSilentToken()
    const url = `${COP_API_BASE_URL}${USERS_ENDPOINT}/ExportUsers?exportAll=${userIds.length === 0}`
    const headers = buildCopHeaders(token)

    const response = await axios.post<Blob>(url, { userIds }, { headers, responseType: 'blob' })

    const blob = new Blob([response.data], { type: response.headers['content-type'] as string })
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    const today = new Date().toISOString().slice(0, 10);
    link.download = `COP_Users_Details_${today}.xlsx`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(objectUrl)
  } catch (error) {
    window.console.error('Export users error:', error)
  }
}

export async function deleteUser(userId: string | number): Promise<boolean> {
  try {
    const token = getSilentToken()
    const url = `${COP_API_BASE_URL}${USERS_ENDPOINT}/${userId}`
    const headers = buildCopHeaders(token)

    await axios.delete(url, { headers })
    return true
  } catch (error) {
    // eslint-disable-next-line no-console
    window.console.error('Delete user API error:', error)
    return false
  }
}

export interface CreateUserPayload {
  cspAccess: boolean
  email: string
  firstName: string
  function: string
  isGAShieldUser: boolean
  isMultiTenantUser: boolean
  isSystemAdmin: boolean
  isUserManager: boolean
  lastname: string
  partnerPortalAccess: string
  products: string[]
  psaAccess: boolean
}

export async function createUser(payload: CreateUserPayload): Promise<User> {
  return post<User>(`${COP_API_BASE_URL}${USERS_ENDPOINT}`, payload)
}

export async function fetchUserById(id: string | number): Promise<User | null> {
  try {
    return await get<User>(`${COP_API_BASE_URL}${USERS_ENDPOINT}/${id}`)
  } catch (error) {
    window.console.error('Fetch user by ID error:', error)
    return null
  }
}

export async function updateUser(id: string | number, payload: CreateUserPayload): Promise<User> {
  return put<User>(`${COP_API_BASE_URL}${USERS_ENDPOINT}/${id}`, payload)
}

export interface CustomerUserRoleItem {
  customerId: string
  roleId: string | number
  products: number[]
}

export async function updateCustomerUserRoles(
  userId: string | number,
  roles: CustomerUserRoleItem[],
): Promise<void> {
  await put<void>(
    `${COP_API_BASE_URL}${USERS_ENDPOINT}/${userId}/customerUserRoles`,
    { customerUserRoles: roles },
  )
}

export async function fetchCustomerUserRoles(
  userId: string | number,
): Promise<CustomerUserRoleItem[]> {
  try {
    const data = await get<{ customerUserRoles: CustomerUserRoleItem[] } | CustomerUserRoleItem[]>(
      `${COP_API_BASE_URL}${USERS_ENDPOINT}/${userId}/customerUserRoles`,
    )
    return Array.isArray(data) ? data : (data.customerUserRoles ?? [])
  } catch {
    return []
  }
}

export async function checkEmailExists(
  email: string,
  excludeUserId?: string | number,
): Promise<boolean> {
  try {
    const url = `${COP_API_BASE_URL}${USERS_ENDPOINT}?email=${encodeURIComponent(email)}`
    const data = await get<UserListResponse | User[]>(url)
    const users: User[] = Array.isArray(data) ? data : (data as UserListResponse).value ?? []
    return excludeUserId
      ? users.some(u => String(u.id) !== String(excludeUserId))
      : users.length > 0
  } catch {
    return false
  }
}

